import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import { setThreePositionFromEnu } from './ScoutView';

const disposeObjectResources = (object: THREE.Object3D): void => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    geometries.add(child.geometry);
    const childMaterials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of childMaterials) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
};

/** Small procedural radar mast whose dish scans continuously about world up. */
export class RadarSiteView extends THREE.Group {
  readonly dish = new THREE.Group();
  private disposed = false;

  constructor(mountEnu: Vec3) {
    super();
    this.name = 'radar-site';
    setThreePositionFromEnu(this, mountEnu);

    const baseMaterial = new THREE.MeshStandardMaterial({
      color: 0x58636d,
      roughness: 0.7,
      metalness: 0.25,
    });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.42, 0.18, 12), baseMaterial);
    base.name = 'radar:base';
    base.rotation.x = Math.PI / 2;
    base.position.z = 0.09;
    base.castShadow = true;
    this.add(base);

    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 0.8, 10), baseMaterial);
    mast.name = 'radar:mast';
    mast.rotation.x = Math.PI / 2;
    mast.position.z = 0.55;
    mast.castShadow = true;
    this.add(mast);

    this.dish.name = 'radar:dish-azimuth';
    this.dish.position.z = 1;
    const dishMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4dde2,
      roughness: 0.42,
      metalness: 0.45,
      side: THREE.DoubleSide,
    });
    const reflector = new THREE.Mesh(
      new THREE.SphereGeometry(0.42, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      dishMaterial,
    );
    reflector.name = 'radar:reflector';
    reflector.scale.set(1, 1, 0.28);
    reflector.rotation.x = Math.PI / 3;
    reflector.castShadow = true;
    this.dish.add(reflector);
    this.add(this.dish);
  }

  update(dtSeconds: number): void {
    const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
    this.dish.rotation.z += dt * 0.8;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    disposeObjectResources(this);
    this.clear();
  }
}

export const createHomeMarker = (homeEnu: Vec3): THREE.Group => {
  const marker = new THREE.Group();
  marker.name = 'scout-home';
  setThreePositionFromEnu(marker, homeEnu);

  const material = new THREE.MeshBasicMaterial({
    color: 0x66ff99,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.45, 0.53, 32), material);
  ring.name = 'scout-home:ring';
  ring.position.z = 0.015;
  marker.add(ring);

  const crossMaterial = material.clone();
  const horizontal = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.055, 0.018), crossMaterial);
  const vertical = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.72, 0.018), crossMaterial);
  horizontal.name = 'scout-home:cross-east';
  vertical.name = 'scout-home:cross-north';
  horizontal.position.z = 0.018;
  vertical.position.z = 0.018;
  marker.add(horizontal, vertical);
  return marker;
};

export const disposeHomeMarker = (marker: THREE.Group): void => {
  marker.removeFromParent();
  disposeObjectResources(marker);
  marker.clear();
};
